import { Box, styled } from '@mui/material';
import { ProviderOffersI } from 'src/e2e-redesign/business-logic/e2e-configuration-object-logic';
import { LenderOffersDetailedI, LenderOffersI, LenderOfferValueTypesE } from 'src/interfaces/submissions.interfaces';

const TABLE_COLUMNS_ORDER = Object.freeze([
  'product_code',
  'response',
  'max_finance_amount',
  'requested_amount',
  'apr',
  'monthly_payment',
  'term',
]);

enum LenderClubOffesProductCodes {
  LC_FIXED_RATE_PLAN = 'LC_FIXED_RATE_PLAN',
  LC_TRUE_NO_INTEREST = 'LC_TRUE_NO_INTEREST',
  ADS_PROMOTIONAL_NO_INTEREST_PLAN = 'ADS_PROMOTIONAL_NO_INTEREST_PLAN',
  ADS_PROMOTIONAL_RATE_PLAN = 'ADS_PROMOTIONAL_RATE_PLAN',
}

const getProductCodeMessages = (productCode: string) => {
  switch (productCode) {
    case LenderClubOffesProductCodes.LC_FIXED_RATE_PLAN:
    case LenderClubOffesProductCodes.LC_TRUE_NO_INTEREST:
      return { title: 'Fixed Installment Loan', subtitle: 'with LendingClub Bank.' };

    case LenderClubOffesProductCodes.ADS_PROMOTIONAL_NO_INTEREST_PLAN:
      return {
        title: 'Promotional No-Interest Plan Revolving Line Of Credit',
        subtitle: 'with Comenity Capital Bank.',
      };

    case LenderClubOffesProductCodes.ADS_PROMOTIONAL_RATE_PLAN:
      return { title: 'Promotional Rate Plan Revolving Line Of Credit', subtitle: 'with Comenity Capital Bank.' };

    default:
      return { title: '', subtitle: '' };
  }
};

const StyledProductCodeMessage = styled('p')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  textAlign: 'left',
  fontSize: '16px',
}));

const StyledProductCodeMessagSub = styled('p')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 'normal',
  textAlign: 'left',
  fontSize: '12px',
}));

type ExtendedLenderOffersDetailedI = LenderOffersDetailedI & {
  original_product_code?: string;
};

export const changeOrderOfCellsForOffers = (offer: LenderOffersI[] = [], isMobile = false) => {
  const newFpd = offer.map((fpd) => {
    const detailedApp = fpd.fp_detailed_app!;

    const finalOrder: ExtendedLenderOffersDetailedI[] = [];

    TABLE_COLUMNS_ORDER.forEach((column) => {
      if (column === 'response') {
        finalOrder.push({
          id: column,
          label: 'Response',
          value: fpd.current_status || '',
          show: true,
          type: LenderOfferValueTypesE.string,
        });
        return;
      }

      if (column === 'product_code') {
        const productCode = fpd.product_code!;

        finalOrder.push({
          id: column,
          label: isMobile ? '' : 'Product',
          value: (
            <Box sx={{ lineHeight: '1', whiteSpace: 'wrap', minWidth: '250px' }}>
              <StyledProductCodeMessage>{getProductCodeMessages(productCode)?.title || ''}</StyledProductCodeMessage>
              <StyledProductCodeMessagSub sx={{ marginTop: '-10px' }}>
                {getProductCodeMessages(productCode)?.subtitle || ''}
              </StyledProductCodeMessagSub>
            </Box>
          ) as any,
          show: true,
          type: LenderOfferValueTypesE.jsx,
          original_product_code: productCode,
        });

        return;
      }

      const columnData = detailedApp.find((d) => d.id === column);
      if (columnData) {
        finalOrder.push(columnData);
      }
    });

    return {
      ...fpd,
      fp_detailed_app: finalOrder,
    };
  });

  // for mobile, we show all the product offers in each card
  if (isMobile) {
    return newFpd;
  }

  // Remove duplicates based on product_code for desktop view, and only show them grouped by the first one found
  const uniqueProductCodes = new Set();

  const removeDuplicateProductCodesFromFpDetailedApp = newFpd.map((fpd) => {
    // leave an empty string in the value if the jsx was already used
    const productCode = fpd.fp_detailed_app?.find((d) => d.id === 'product_code')?.original_product_code;
    if (productCode && uniqueProductCodes.has(productCode)) {
      fpd.fp_detailed_app = fpd.fp_detailed_app?.map((d) => {
        if (d.id === 'product_code') {
          return { ...d, value: '\u200D' };
        }
        return d;
      });
    }
    if (productCode) {
      uniqueProductCodes.add(productCode);
    }

    return fpd;
  });

  return removeDuplicateProductCodesFromFpDetailedApp;
};

export const changeOrderOfColumnHeadersForOffersDetailed = (offerDetails: LenderOffersDetailedI[] = []) => {
  const finalOrder: LenderOffersDetailedI[] = [];

  TABLE_COLUMNS_ORDER.forEach((column) => {
    if (column === 'response') {
      finalOrder.push({
        id: column,
        label: 'Response',
        value: 'Response',
        show: true,
        type: LenderOfferValueTypesE.string,
      });
      return;
    }

    const columnData = offerDetails.find((d) => d.id === column);
    if (columnData) {
      finalOrder.push(columnData);
    }
  });

  return finalOrder;
};

export const modifyOffersOrder = (offer: ProviderOffersI, isMobile = false): ProviderOffersI => {
  return {
    ...offer,
    sub_details: {
      ...offer.sub_details,
      lender_offers: changeOrderOfCellsForOffers(offer.sub_details.lender_offers!, isMobile),
    },
  };
};
